import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { createContact } from "@/api/contacts";
import type { Company, Contact } from "@/api/types";

// We can't make `company_id` conditionally required at the zod level cleanly
// without a discriminated union, so we always parse the form, then validate
// the company_id manually before submit (the combobox enforces selection).
const schema = z.object({
  full_name: z.string().min(1, "Name is required").max(255),
  title: z.string().nullable(),
  email: z
    .union([z.literal(""), z.string().email("Invalid email")])
    .nullable(),
  phone: z.string().nullable(),
  linkedin_url: z
    .union([z.literal(""), z.string().url("Invalid URL")])
    .nullable(),
  is_decision_maker: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

interface CreateContactDialogProps {
  projectSlug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided, the company is pre-selected and locked. */
  company?: Company | null;
  /** Required when `company` is not provided — used by the combobox. */
  companies?: Company[];
  onCreated: (contact: Contact) => void;
}

export function CreateContactDialog({
  projectSlug,
  open,
  onOpenChange,
  company,
  companies = [],
  onCreated,
}: CreateContactDialogProps) {
  const [companyId, setCompanyId] = useState<number | null>(
    company?.id ?? null,
  );
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [companyPickerOpen, setCompanyPickerOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      full_name: "",
      title: null,
      email: null,
      phone: null,
      linkedin_url: null,
      is_decision_maker: false,
    },
  });

  // Reset form + selection on open/close.
  useEffect(() => {
    if (open) {
      reset({
        full_name: "",
        title: null,
        email: null,
        phone: null,
        linkedin_url: null,
        is_decision_maker: false,
      });
      setCompanyId(company?.id ?? null);
      setCompanyError(null);
    }
  }, [open, company?.id, reset]);

  const selectedCompany = useMemo(() => {
    if (company) return company;
    return companies.find((c) => c.id === companyId) ?? null;
  }, [company, companies, companyId]);

  async function onSubmit(values: FormValues) {
    const targetId = company?.id ?? companyId;
    if (!targetId) {
      setCompanyError("Pick a company");
      return;
    }
    try {
      const created = await createContact(projectSlug, {
        company_id: targetId,
        full_name: values.full_name.trim(),
        title: emptyToNull(values.title),
        email: emptyToNull(values.email),
        phone: emptyToNull(values.phone),
        linkedin_url: emptyToNull(values.linkedin_url),
        is_decision_maker: values.is_decision_maker,
      });
      toast.success("Contact created");
      onCreated(created);
      onOpenChange(false);
    } catch (err) {
      toast.error(extractMessage(err));
    }
  }

  const isDM = watch("is_decision_maker");
  const companyLocked = !!company;

  return (
    <Dialog open={open} onOpenChange={(o) => !isSubmitting && onOpenChange(o)}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>New contact</DialogTitle>
          <DialogDescription>
            Add a person to a company in this project.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-5"
          noValidate
        >
          <Field label="Company" error={companyError ?? undefined}>
            {companyLocked ? (
              <div className="flex h-10 items-center rounded-lg border border-slate-200 bg-slate-50/60 px-3 text-sm text-slate-700">
                <span className="truncate">{company?.name}</span>
                {company?.domain && (
                  <span className="ml-2 truncate text-xs text-slate-400">
                    {company.domain}
                  </span>
                )}
              </div>
            ) : (
              <Popover
                open={companyPickerOpen}
                onOpenChange={setCompanyPickerOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={companyPickerOpen}
                    className="h-10 w-full justify-between rounded-lg border-slate-200 bg-white font-normal"
                  >
                    {selectedCompany ? (
                      <span className="flex min-w-0 items-baseline gap-2">
                        <span className="truncate text-slate-900">
                          {selectedCompany.name}
                        </span>
                        {selectedCompany.domain && (
                          <span className="truncate text-xs text-slate-400">
                            {selectedCompany.domain}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="text-slate-400">Pick a company</span>
                    )}
                    <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 text-slate-400" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                >
                  <Command>
                    <CommandInput placeholder="Search companies…" />
                    <CommandList>
                      <CommandEmpty>No company found.</CommandEmpty>
                      <CommandGroup>
                        {companies.map((c) => (
                          <CommandItem
                            key={c.id}
                            value={`${c.name} ${c.domain ?? ""}`}
                            onSelect={() => {
                              setCompanyId(c.id);
                              setCompanyError(null);
                              setCompanyPickerOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-3.5 w-3.5",
                                companyId === c.id
                                  ? "opacity-100"
                                  : "opacity-0",
                              )}
                            />
                            <span className="min-w-0 truncate">{c.name}</span>
                            {c.domain && (
                              <span className="ml-2 truncate text-xs text-slate-400">
                                {c.domain}
                              </span>
                            )}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </Field>

          <Field label="Full name" error={errors.full_name?.message}>
            <Input
              {...register("full_name")}
              autoFocus
              className="h-10 rounded-lg border-slate-200"
              placeholder="Jane Doe"
            />
          </Field>

          <Field label="Title" error={errors.title?.message}>
            <Input
              {...register("title")}
              className="h-10 rounded-lg border-slate-200"
              placeholder="CEO, CFO…"
            />
          </Field>

          <Field label="Email" error={errors.email?.message}>
            <Input
              type="email"
              {...register("email")}
              className="h-10 rounded-lg border-slate-200"
              placeholder="name@company.com"
            />
          </Field>

          <Field label="Phone" error={errors.phone?.message}>
            <Input
              type="tel"
              {...register("phone")}
              className="h-10 rounded-lg border-slate-200"
              placeholder="+1 415 555 0100"
            />
          </Field>

          <Field label="LinkedIn" error={errors.linkedin_url?.message}>
            <Input
              type="url"
              {...register("linkedin_url")}
              className="h-10 rounded-lg border-slate-200"
              placeholder="https://linkedin.com/in/…"
            />
          </Field>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2.5">
            <div>
              <div className="text-sm font-medium text-slate-900">
                Decision maker
              </div>
              <p className="text-xs text-slate-500">
                Mark as a key contact for sales
              </p>
            </div>
            <Switch
              checked={isDM}
              onCheckedChange={(v) =>
                setValue("is_decision_maker", v, { shouldDirty: true })
              }
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            variant="primary"
            >
              {isSubmitting && (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              )}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
        {label}
      </label>
      {children}
      {error && <p className="text-xs text-rose-600">{error}</p>}
    </div>
  );
}

function emptyToNull(v: string | null | undefined): string | null {
  if (v === null || v === undefined) return null;
  return v.trim() === "" ? null : v.trim();
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.response?.status === 409) {
      return "A contact with this email already exists for this company.";
    }
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: string };
      if (first?.msg) return first.msg;
    }
  }
  return "Couldn't create the contact.";
}
